import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, CheckCircle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { useGoogleReviews } from '../seo/googleReviews';

// Les avis proviennent de la fiche Google via /api/reviews : aucun avis n'est rédigé en interne
const fallbackReviews = [];

const SocialProof = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const google = useGoogleReviews();
  const scrollRef = useRef(null);

  const reviews = useMemo(() => (google ? google.reviews.map((review, index) => ({
    id: review.uri || `google-${index}`,
    name: review.author || 'Client Google',
    location: review.relativeTime || 'Avis Google',
    rating: review.rating || 5,
    text: review.text,
    verified: true,
    uri: review.uri,
  })) : fallbackReviews), [google]);

  // Auto-scroll every 4 seconds
  useEffect(() => {
    if (!isAutoPlaying || reviews.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, reviews.length]);

  // Scroll to current index
  useEffect(() => {
    if (scrollRef.current) {
      const cardWidth = 320 + 24; // card width + gap
      scrollRef.current.scrollTo({
        left: currentIndex * cardWidth,
        behavior: 'smooth'
      });
    }
  }, [currentIndex]);

  const goToPrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  if (reviews.length === 0) return null;

  return (
    <section className="py-16 bg-slate-50 overflow-hidden" id="avis">
      <div className="max-w-6xl mx-auto px-4">
        
        {/* Header compact */}
        <div className="text-center mb-10">
          {google?.rating ? (
            <div className="inline-flex items-center gap-3 mb-4">
              <img
                src="https://www.google.com/favicon.ico"
                alt="Google"
                className="w-6 h-6"
              />
              <span className="text-3xl font-black text-slate-800">{Number(google.rating).toFixed(1)}</span>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} size={20} className="fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-slate-500">{google.userRatingCount ? `sur ${google.userRatingCount} avis Google` : 'sur Google'}</span>
            </div>
          ) : (
            <p className="text-slate-500 text-sm mb-4">Avis publiés sur notre fiche Google</p>
          )}
          <h2 className="text-2xl font-bold text-slate-800">
            Ce que disent nos clients
          </h2>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button 
            onClick={goToPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors -ml-2"
            aria-label="Avis précédent"
          >
            <ChevronLeft size={20} className="text-slate-600" />
          </button>
          
          <button 
            onClick={goToNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors -mr-2"
            aria-label="Avis suivant"
          >
            <ChevronRight size={20} className="text-slate-600" />
          </button>

          {/* Scrollable Reviews */}
          <div 
            ref={scrollRef}
            className="flex gap-6 overflow-x-hidden px-8 py-4 scroll-smooth"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            {reviews.map((review) => (
              <div 
                key={review.id}
                className="flex-shrink-0 w-80 bg-white rounded-xl p-6 shadow-md border border-slate-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(Math.round(review.rating || 5))].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 min-h-[60px]">
                  "{review.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-slate-800">{review.name}</span>
                      {review.verified && google && <CheckCircle size={14} className="text-green-500" aria-label="Avis publié sur Google" />}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <MapPin size={10} />
                      {review.location}
                    </div>
                  </div>
                </div>
                {review.uri && (
                  <a href={review.uri} target="_blank" rel="noopener noreferrer nofollow" className="mt-4 inline-flex items-center gap-1 text-xs text-green-700 hover:text-green-800 transition-colors">
                    Voir sur Google Maps <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setIsAutoPlaying(false);
                  setCurrentIndex(index);
                }}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'bg-green-500 w-6' 
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                aria-label={`Aller à l'avis ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-10 flex flex-wrap justify-center gap-4">
          <Link
            to="/avis-clients"
            data-testid="social-proof-avis-page-link"
            className="inline-flex items-center gap-2 px-6 py-3 border border-green-600 text-green-700 font-semibold rounded-lg hover:bg-green-50 transition-colors"
          >
            Tous les avis clients
          </Link>
          <a 
            href="https://www.google.com/maps/search/3DLoc+Cauville-sur-Mer" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
          >
            <img src="https://www.google.com/favicon.ico" alt="Google" className="w-4 h-4" />
            Voir tous les avis
            <ExternalLink size={16} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default SocialProof;
